import { Box, Flex, Text, Image } from "@chakra-ui/react";

import {
  accDiv,
  accMul,
  formatAmountWithFixed,
  toFixed,
} from "@/utils/formatBalance";
import { TokenInfo, useUserInfo } from "@/context/UserInfo";
import { usdcInfo } from "@/constants/tokens";
import { GoIcon } from "@/components/icons";
import { useCurrencyExchange } from "@/hooks/useCurrencyExchange";
import { shortenAddress } from "@/utils";
import { Order } from "@/api/order";
import { getDidName } from "@/utils/didName";

const DetailWrap = ({
  name,
  children,
}: {
  name: string;
  children: React.ReactNode;
}) => {
  return (
    <Flex
      align={"center"}
      justify={"space-between"}
      sx={{
        width: "100%",
      }}
    >
      <Text
        sx={{
          color: "#FFD096",
          fontSize: "16px",
          fontWeight: 900,
          fontFamily: "DIN-Black",
        }}
      >
        {name}
      </Text>
      <Box
        sx={{
          textAlign: "right",
          flex: 1,
        }}
      >
        {children}
      </Box>
    </Flex>
  );
};

const PayDetail = ({
  isNormalPay,
  networkFeeAmount,
  networkFeeValue,
  selectedToken,
  onSelectAssetOpen,
  currentTip,
  orderDetail,
  balanceDue,
}: {
  isNormalPay: boolean;
  networkFeeAmount: string;
  networkFeeValue: string;
  selectedToken: TokenInfo | null;
  onSelectAssetOpen: () => void;
  currentTip: string;
  orderDetail: Order | null;
  balanceDue: string;
}) => {
  const { usdToCurrency, currencyUnit } = useCurrencyExchange();

  return (
    <Box
      sx={{
        paddingBottom: "20px",
        width: "100%",
      }}
    >
      <Text
        sx={{
          marginTop: "20px",
          fontSize: "18px",
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        {orderDetail
          ? getDidName(orderDetail?.recipient?.didName)
            ? getDidName(orderDetail?.recipient?.didName)
            : shortenAddress(orderDetail.recipient.address)
          : ""}
      </Text>
      <Flex
        align={"center"}
        flexDir={"column"}
        gap={"20px"}
        sx={{
          marginTop: "12px",
        }}
      >
        <DetailWrap name="Subtotal:">
          <Text
            sx={{
              fontSize: "16px",
              fontWeight: 900,
              fontFamily: "DIN-Black",
            }}
          >
            {orderDetail
              ? formatAmountWithFixed(
                  usdToCurrency(orderDetail.orderAmount),
                  usdcInfo.decimals
                )
              : "0"}{" "}
            <span style={{ fontSize: "12px" }}>{currencyUnit}</span>
          </Text>
        </DetailWrap>
        {isNormalPay && (
          <DetailWrap name="Tax:">
            <Text
              sx={{
                fontSize: "16px",
                fontWeight: 900,
                fontFamily: "DIN-Black",
              }}
            >
              {orderDetail
                ? formatAmountWithFixed(
                    usdToCurrency(orderDetail.taxAmount),
                    usdcInfo.decimals
                  )
                : "0"}{" "}
              <span style={{ fontSize: "12px" }}>{currencyUnit}</span>
            </Text>
            <Text
              sx={{
                fontSize: "12px",
              }}
            >
              ({orderDetail?.taxRate}%)
            </Text>
          </DetailWrap>
        )}
        {isNormalPay && (
          <DetailWrap name="Tips:">
            <Text
              sx={{
                fontSize: "16px",
                fontWeight: 900,
                fontFamily: "DIN-Black",
              }}
            >
              {formatAmountWithFixed(
                usdToCurrency(currentTip),
                usdcInfo.decimals
              )}{" "}
              <span style={{ fontSize: "12px" }}>{currencyUnit}</span>
            </Text>
            {orderDetail && (
              <Text
                sx={{
                  fontSize: "12px",
                }}
              >
                {currentTip === "0"
                  ? "0.00"
                  : toFixed(
                      accMul(accDiv(currentTip, orderDetail.orderAmount), 100),
                      2
                    )}
                %
              </Text>
            )}
          </DetailWrap>
        )}
        <DetailWrap name="Network Fee:">
          {selectedToken?.address === usdcInfo.address ? (
            <Text sx={{ color: "#2FFFA5" }}>Free</Text>
          ) : (
            <>
              <Text
                sx={{
                  fontWeight: 900,
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontFamily: "DIN-Black",
                  }}
                >
                  {formatAmountWithFixed(
                    usdToCurrency(networkFeeValue),
                    usdcInfo.decimals
                  )}{" "}
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    fontFamily: "DIN-Black",
                  }}
                >
                  {currencyUnit}
                </span>
              </Text>
              <Text
                sx={{
                  fontSize: "12px",
                }}
              >
                ({formatAmountWithFixed(networkFeeAmount)} ETH)
              </Text>
            </>
          )}
        </DetailWrap>
        <DetailWrap name="Balance Due:">
          <Text
            sx={{
              fontSize: "36px",
              fontWeight: 900,
              fontFamily: "DIN-Black",
            }}
          >
            {formatAmountWithFixed(
              usdToCurrency(balanceDue),
              usdcInfo.decimals
            )}{" "}
            <span style={{ fontSize: "16px" }}>{currencyUnit}</span>
          </Text>
        </DetailWrap>
      </Flex>

      <Flex
        align={"center"}
        justify={"space-between"}
        sx={{
          marginTop: "24px",
          justifyContent: "center",
        }}
      >
        <Text
          sx={{
            fontSize: "20px",
            marginRight: "10px",
            fontFamily: "DIN-Black",
            fontWeight: 900,
          }}
        >
          Pay with
        </Text>
        <Flex
          onClick={onSelectAssetOpen}
          sx={{
            width: "75px",
            height: "35px",
            background: "#FFD096",
            borderRadius: "24px",
          }}
          justify={"space-between"}
          align={"center"}
        >
          <Image
            src={selectedToken?.icon}
            sx={{
              height: "35px",
            }}
          ></Image>
          <GoIcon
            style={{
              width: "12px",
              marginRight: "12px",
              color: "#D8572B",
            }}
          ></GoIcon>
        </Flex>
      </Flex>
      <Box
        sx={{
          color: "#FFD096",
          fontSize: "12px",
        }}
      >
        <Text
          sx={{
            fontWeight: 500,
            textAlign: "center",
            marginTop: "8px",
          }}
        >
          {selectedToken && (
            <span>
              {" "}
              <span
                style={{
                  fontSize: "16px",
                  fontFamily: "DIN-Black",
                }}
              >
                {formatAmountWithFixed(
                  selectedToken.balance,
                  selectedToken.decimals
                )}{" "}
              </span>
              <span>
                {selectedToken.symbol} (
                {formatAmountWithFixed(
                  usdToCurrency(selectedToken.value),
                  usdcInfo.decimals
                )}
                )
              </span>
            </span>
          )}
        </Text>
        <Text
          sx={{
            color: "#FFD096",
            textAlign: "center",
            fontWeight: 500,
          }}
        >
          Available
        </Text>
      </Box>
    </Box>
  );
};

export default PayDetail;
